import {
    BarChart3,
    Mail,
    FlaskConical,
    Eye,
    Users,
    User,
    Zap,
    History,
    LayoutTemplate,
    HelpCircle,
    Brain,
    CheckCircle,
    Target,
    Activity,
    TrendingUp,
    Sparkles,
    Rocket,
    IdCard,
    ServerCog,
    Recycle,
    Settings,
    CreditCard,
    ReceiptText,
    LifeBuoy,
    CodeXml,
    MessageSquareText,
    MessageSquare,
    FileText,
    type LucideIcon,
} from "lucide-react";
import { isDevMode } from "lib/featureFlags";

/* ══════════════════════════════════════════════════════════════════════════
   Navigation registry — one source of truth.

   The sidebar's nav list, the ⌘K command palette's list and the header's page
   title were three separate hand-maintained arrays that had already drifted:
   the palette knew about 18 destinations the sidebar didn't group, and the
   sidebar's AI-agent links pointed at `/agents/*` paths that no route ever
   matched (every one of them silently redirected to the dashboard).

   Everything navigational now derives from this file.
   ══════════════════════════════════════════════════════════════════════════ */

export interface NavItem {
    id: string;
    label: string;
    icon: LucideIcon;
    path: string;
    /** Shown in the command palette and as the page description */
    description?: string;
    /** Extra terms the command palette should match on */
    keywords?: string[];
    /** Hidden from the sidebar but still reachable + searchable */
    hiddenInSidebar?: boolean;
    /** Only rendered when REACT_APP_SHOW_DEV_FEATURES is on */
    devOnly?: boolean;
    /** Pulsing indicator, e.g. live monitoring */
    live?: boolean;
    /** Paths that should light this item up as active */
    matches?: string[];
}

export interface NavSection {
    id: string;
    /** Omit for the top section, which needs no label */
    label?: string;
    items: NavItem[];
    devOnly?: boolean;
    /** Renders as an expandable group rather than a flat list */
    collapsible?: boolean;
}

/* ── Primary navigation ─────────────────────────────────────────────────
   Grouped by the job the user is doing, not by when the feature was built.
   Fourteen flat entries with no headings was the single biggest reason the
   old sidebar was hard to scan.                                          */

export const NAV_SECTIONS: NavSection[] = [
    {
        id: "overview",
        items: [
            {
                id: "dashboard",
                label: "Dashboard",
                icon: BarChart3,
                path: "/dashboard",
                description: "Marketing overview",
                keywords: ["home", "overview", "stats", "metrics", "kpi"],
            },
            {
                id: "ai-playground",
                label: "AI Assistant",
                icon: MessageSquareText,
                path: "/ai-playground",
                description: "Ask, draft and analyse",
                keywords: ["chat", "ai", "assistant", "copilot", "generate"],
            },
        ],
    },
    {
        id: "create",
        label: "Create",
        items: [
            {
                id: "templates",
                label: "Templates",
                icon: LayoutTemplate,
                path: "/templates",
                description: "Email templates",
                keywords: ["email", "design", "editor", "layout"],
                matches: ["/templates", "/templates/editor", "/content-builder", "/template-list"],
            },
            {
                id: "campaigns",
                label: "Campaigns",
                icon: Mail,
                path: "/campaigns",
                description: "Create and manage campaigns",
                keywords: ["email", "send", "blast", "broadcast"],
                matches: ["/campaigns", "/campaigns/new", "/campaigns/flow"],
            },
            {
                id: "launch",
                label: "Launch",
                icon: Rocket,
                path: "/launch",
                description: "Send a campaign",
                keywords: ["send", "deliver", "publish", "schedule"],
            },
        ],
    },
    {
        id: "audience",
        label: "Audience",
        items: [
            {
                id: "leads",
                label: "Leads",
                icon: User,
                path: "/leads",
                description: "Contacts and subscribers",
                keywords: ["contacts", "subscribers", "people", "customers"],
                matches: ["/leads"],
            },
            {
                id: "audiences",
                label: "Audiences",
                icon: Users,
                path: "/audiences",
                description: "Segments and lists",
                keywords: ["segments", "groups", "lists", "targeting"],
            },
        ],
    },
    {
        id: "optimize",
        label: "Optimise",
        items: [
            {
                id: "ab-testing",
                label: "A/B Testing",
                icon: FlaskConical,
                path: "/ab-testing",
                description: "Experiments and variants",
                keywords: ["experiment", "variant", "split", "test"],
            },
            {
                id: "live-monitoring",
                label: "Live Monitoring",
                icon: Eye,
                path: "/live-monitoring",
                description: "Real-time campaign activity",
                keywords: ["realtime", "live", "monitor", "activity"],
                devOnly: true,
                live: true,
            },
        ],
    },
    {
        id: "configure",
        label: "Configure",
        items: [
            {
                id: "identity",
                label: "Identity",
                icon: IdCard,
                path: "/identity",
                description: "Sender domains and addresses",
                keywords: ["sender", "domain", "dkim", "verify", "from"],
            },
            {
                id: "integrations",
                label: "Integrations",
                icon: Zap,
                path: "/integrations",
                description: "Connected services",
                keywords: ["connect", "api", "webhook", "shopify", "crm", "salesforce"],
            },
            {
                id: "widgets",
                label: "Widgets",
                icon: ServerCog,
                path: "/widgets",
                description: "Embeddable sign-up forms",
                keywords: ["embed", "form", "signup", "popup", "banner"],
            },
        ],
    },
    {
        id: "records",
        label: "Records",
        items: [
            {
                id: "history",
                label: "History",
                icon: History,
                path: "/history",
                description: "Activity log",
                keywords: ["log", "activity", "recent", "audit"],
            },
            {
                id: "trash",
                label: "Trash",
                icon: Recycle,
                path: "/trash",
                description: "Deleted items you can restore",
                keywords: ["deleted", "remove", "restore", "recover"],
            },
        ],
    },
    {
        id: "agents",
        label: "AI Agents",
        devOnly: true,
        collapsible: true,
        items: [
            {
                id: "agent",
                label: "Agent",
                icon: Brain,
                path: "/agent",
                description: "Agent workspace",
                devOnly: true,
            },
            {
                id: "ai-agents",
                label: "Agent Dashboard",
                icon: Activity,
                path: "/ai-agents",
                description: "Fleet overview",
                devOnly: true,
            },
            {
                id: "agent-collaboration",
                label: "Collaboration",
                icon: Users,
                path: "/agent-collaboration",
                description: "How agents work together",
                devOnly: true,
            },
            {
                id: "decision-explainer",
                label: "Decision Explainer",
                icon: Target,
                path: "/decision-explainer",
                description: "Why an agent chose what it chose",
                devOnly: true,
            },
            {
                id: "reasoning-trails",
                label: "Reasoning Trails",
                icon: CheckCircle,
                path: "/reasoning-trails",
                description: "Step-by-step agent reasoning",
                devOnly: true,
            },
            {
                id: "impact-tracker",
                label: "Impact Tracker",
                icon: TrendingUp,
                path: "/impact-tracker",
                description: "Measured agent impact",
                devOnly: true,
            },
            {
                id: "learning-progression",
                label: "Learning Progress",
                icon: Sparkles,
                path: "/learning-progression",
                description: "Capability growth over time",
                devOnly: true,
            },
        ],
    },
];

/* ── Account destinations ────────────────────────────────────────────────
   Reachable from the user menu and the command palette; deliberately kept
   out of the primary rail so it stays about the work, not the settings.  */

export const ACCOUNT_ITEMS: NavItem[] = [
    {
        id: "profile",
        label: "Profile & Settings",
        icon: Settings,
        path: "/profile",
        description: "Account preferences",
        keywords: ["account", "profile", "preferences", "settings", "password"],
        hiddenInSidebar: true,
    },
    {
        id: "billing",
        label: "Billing & Plans",
        icon: CreditCard,
        path: "/payments",
        description: "Subscription and payment methods",
        keywords: ["plan", "subscription", "payment", "credits", "upgrade", "card"],
        hiddenInSidebar: true,
    },
    {
        id: "transactions",
        label: "Transactions",
        icon: ReceiptText,
        path: "/payments?tab=transactions",
        description: "Payment history and invoices",
        keywords: ["payment", "invoice", "receipt", "history", "billing"],
        hiddenInSidebar: true,
    },
    {
        id: "developer",
        label: "Developer Settings",
        icon: CodeXml,
        path: "/developer",
        description: "API keys and webhooks",
        keywords: ["api", "key", "webhook", "token", "developer"],
        hiddenInSidebar: true,
    },
];

export const SUPPORT_ITEMS: NavItem[] = [
    {
        id: "support",
        label: "Support",
        icon: LifeBuoy,
        path: "/support",
        description: "Tickets and assistance",
        keywords: ["help", "ticket", "contact", "issue"],
        hiddenInSidebar: true,
    },
    {
        id: "help",
        label: "Help Center",
        icon: HelpCircle,
        path: "/help",
        description: "Guides and documentation",
        keywords: ["docs", "guide", "faq", "documentation"],
        hiddenInSidebar: true,
    },
    {
        id: "feedback",
        label: "Feedback",
        icon: MessageSquare,
        path: "/feedback",
        description: "Share your thoughts",
        keywords: ["suggest", "report", "feature", "idea"],
        hiddenInSidebar: true,
    },
    {
        id: "document",
        label: "Documents",
        icon: FileText,
        path: "/document",
        description: "Reference documents",
        keywords: ["doc", "file", "reference"],
        hiddenInSidebar: true,
    },
];

/* ── Derived views ───────────────────────────────────────────────────── */

/** Sections filtered by the dev-features flag, ready to render. */
export function getNavSections(): NavSection[] {
    const dev = isDevMode();
    return NAV_SECTIONS.filter((section) => !section.devOnly || dev)
        .map((section) => ({
            ...section,
            items: section.items.filter((item) => !item.devOnly || dev),
        }))
        .filter((section) => section.items.length > 0);
}

/** Every navigable destination, including account/support. */
export function getAllDestinations(): NavItem[] {
    return [
        ...getNavSections().flatMap((s) => s.items),
        ...ACCOUNT_ITEMS,
        ...SUPPORT_ITEMS,
    ];
}

/**
 * Longest-prefix match, so `/campaigns/new` resolves to Campaigns rather than
 * falling through to nothing. Used for the active nav item and page title.
 */
export function findNavItemByPath(pathname: string): NavItem | undefined {
    const all = [...NAV_SECTIONS.flatMap((s) => s.items), ...ACCOUNT_ITEMS, ...SUPPORT_ITEMS];
    let best: NavItem | undefined;
    let bestLength = -1;

    for (const item of all) {
        const candidates = item.matches ?? [item.path];
        for (const candidate of candidates) {
            const isMatch =
                pathname === candidate ||
                (candidate !== "/" && pathname.startsWith(candidate + "/"));
            if (isMatch && candidate.length > bestLength) {
                best = item;
                bestLength = candidate.length;
            }
        }
    }
    return best;
}

export function isNavItemActive(item: NavItem, pathname: string): boolean {
    const active = findNavItemByPath(pathname);
    return active?.id === item.id;
}

/* ── Breadcrumbs ─────────────────────────────────────────────────────────
   Sub-route labels, so a deep route can say where it sits rather than just
   inheriting its section's name.

   Patterns use `:param` for a dynamic segment. Order matters only in that the
   most specific match wins, which `buildBreadcrumb` resolves by segment count.
   ───────────────────────────────────────────────────────────────────────── */

interface SubRoute {
    /** Path pattern, e.g. "/leads/manage-groups/:id/:name" */
    pattern: string;
    label: string;
}

const SUB_ROUTES: SubRoute[] = [
    { pattern: "/campaigns/new", label: "New campaign" },
    { pattern: "/campaigns/flow", label: "Campaign flow" },
    { pattern: "/templates/editor/:templateId", label: "Editor" },
    { pattern: "/leads/new", label: "New lead" },
    { pattern: "/leads/manage-groups/:id/:name", label: "Edit group" },
    { pattern: "/leads/:group/:groupName/new", label: "New lead" },
    { pattern: "/leads/:group/:groupName/:id", label: "Edit lead" },
];

function patternMatches(pattern: string, pathname: string): boolean {
    const a = pattern.split("/").filter(Boolean);
    const b = pathname.split("/").filter(Boolean);
    if (a.length !== b.length) return false;
    return a.every((seg, i) => seg.startsWith(":") || seg === b[i]);
}

export interface BreadcrumbCrumb {
    label: string;
    /** Present on every crumb except the last, which is the current page */
    to?: string;
}
export function buildBreadcrumb(pathname: string): BreadcrumbCrumb[] {
    const section = findNavItemByPath(pathname);
    if (!section) return [];

    if (pathname === section.path) return [];

    const sub = SUB_ROUTES.find((r) => patternMatches(r.pattern, pathname));
    if (!sub) return [];

    // The section crumb links back to the list — a dependable way out of any
    // form or editor, on every route, without hunting for a Cancel button.
    return [
        { label: section.label, to: section.path },
        { label: sub.label },
    ];
}
